import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  FormControlLabel,
  Switch,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid2 as Grid,
  SelectChangeEvent,
  Box,
  Alert,
  LinearProgress,
  CircularProgress,
} from "@mui/material";

import { DateTimePicker } from "@mui/x-date-pickers/DateTimePicker";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { styled } from "@mui/material/styles";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faCloudArrowUp } from "@fortawesome/free-solid-svg-icons";
import dayjs, { Dayjs } from "dayjs";
import Course from "../../models/Course";
import useNotification from "../../hooks/useNotification";

const VisuallyHiddenInput = styled("input")({
  clip: "rect(0 0 0 0)",
  clipPath: "inset(50%)",
  height: 1,
  overflow: "hidden",
  position: "absolute",
  bottom: 0,
  left: 0,
  whiteSpace: "nowrap",
  width: 1,
});

interface CreateCourseDialogProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (
    formData: FormData
  ) => Promise<{ success: boolean; message: string }>;
  course?: Course | null; // Add this prop for editing
}

const initialFormState = {
  name: "",
  location: "",
  type: "",
  startDate: null as Dayjs | null,
  endDate: null as Dayjs | null,
  pointOfContact: "",
  registrationCode: "",
  password: "",
  maxStudents: 0,
  openEnroll: false,
  visible: true,
};

const CreateCourseDialog: React.FC<CreateCourseDialogProps> = ({
  open,
  onClose,
  onSubmit,
  course,
}) => {
  const [formState, setFormState] = useState(initialFormState);
  const [file, setFile] = useState<File | null>(null);
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(false);

  const { showNotification } = useNotification(); // Use the notification hook

  // Effect to populate form when editing a course
  useEffect(() => {
    if (course) {
      setFormState({
        name: course.name,
        location: course.location,
        type: course.type,
        startDate: dayjs(course.startDate),
        endDate: dayjs(course.endDate),
        pointOfContact: course.pointOfContact,
        registrationCode: course.registrationCode,
        password: course.password,
        maxStudents: course.maxStudents,
        openEnroll: course.openEnroll,
        visible: course.visible,
      });
    } else {
      setFormState(initialFormState);
    }
  }, [course]);

  const requiredFields = [
    "name",
    "type",
    "startDate",
    "endDate",
    "location",
    "pointOfContact",
    "maxStudents",
  ];

  const resetForm = () => {
    setFormState(initialFormState);
    setFile(null);
    setErrors({});
    setSuccessMessage(null);
  };

  const handleChange = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (requiredFields.includes(name) && value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSelectChange = (event: SelectChangeEvent<string>) => {
    const { name, value } = event.target;
    setFormState((prev) => ({ ...prev, [name]: value }));
    if (requiredFields.includes(name) && value.trim() !== "") {
      setErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSwitchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = event.target;
    setFormState((prev) => ({ ...prev, [name]: checked }));
  };

  const handleDateChange =
    (name: "startDate" | "endDate") => (date: Dayjs | null) => {
      setFormState((prev) => ({ ...prev, [name]: date }));
      if (requiredFields.includes(name) && date) {
        setErrors((prev) => ({ ...prev, [name]: "" }));
      }
    };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      setFile(event.target.files[0]);
    }
  };

  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};
    requiredFields.forEach((field) => {
      if (!formState[field as keyof typeof formState]) {
        newErrors[field] = `${
          field.charAt(0).toUpperCase() + field.slice(1)
        } is required`;
      }
    });
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    const formData = new FormData();
    Object.entries(formState).forEach(([key, value]) => {
      if (dayjs.isDayjs(value)) {
        formData.append(key, value.toISOString());
      } else {
        formData.append(key, String(value));
      }
    });
    if (file) {
      formData.append("file", file);
    }
    //existing course, so need to track id
    if (course) {
      formData.append("id", course.id.toString());
    }

    setIsLoading(true);
    try {
      const result = await onSubmit(formData);
      if (result.success) {
        setSuccessMessage(result.message);
        showNotification(result.message, "success");
      } else {
        showNotification(result.message, "error");
      }
    } catch (error) {
      showNotification(
        "An unexpected error occurred. Please try again.",
        "error"
      );
    } finally {
      setIsLoading(false);
    }
  };

  const handleCloseLocal = (event: any, reason: string) => {
    if (reason && reason === "backdropClick") {
      return;
    }
    resetForm(); // Reset the form when closing the dialog
    onClose();
  };

  const generateRegistrationCode = (length: number) => {
    let result = '';
    const characters = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ';
    const charactersLength = characters.length;
    let counter = 0;
    while (counter < length) {
      result += characters.charAt(Math.floor(Math.random() * charactersLength));
      counter += 1;
    }
    const event: any = { target: { name: "registrationCode", value: result} };
    handleChange(event);
  }

  return (
    <LocalizationProvider dateAdapter={AdapterDayjs}>
      <Dialog open={open} onClose={handleCloseLocal} maxWidth="md" fullWidth>
        <DialogTitle>
          {successMessage
            ? "Course Saved"
            : course
            ? "Edit Course"
            : "Create New Course"}
        </DialogTitle>
        {isLoading && <LinearProgress />}
        <DialogContent>
          {successMessage ? (
            <Box sx={{ my: 2 }}>
              <Alert severity="success">{successMessage}</Alert>
            </Box>
          ) : (
            <Grid container spacing={2}>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="name"
                  label="Course Name"
                  value={formState.name}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.name}
                  helperText={errors.name}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="location"
                  label="Location"
                  value={formState.location}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.location}
                  helperText={errors.location}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <FormControl fullWidth margin="normal" error={!!errors.type}>
                  <InputLabel required>Course Type</InputLabel>
                  <Select
                    name="type"
                    value={formState.type}
                    onChange={handleSelectChange}
                    label="Course Type"
                  >
                    <MenuItem value="legacy">Legacy</MenuItem>
                    <MenuItem value="tactical">Tactical</MenuItem>
                    <MenuItem value="online">Online</MenuItem>
                  </Select>
                  {errors.type && (
                    <Box
                      component="span"
                      sx={{ color: "error.main", fontSize: "0.75rem" }}
                    >
                      {errors.type}
                    </Box>
                  )}
                </FormControl>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  required
                  name="pointOfContact"
                  label="Point of Contact"
                  value={formState.pointOfContact}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.pointOfContact}
                  helperText={errors.pointOfContact}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="Start Date"
                  value={formState.startDate}
                  onChange={handleDateChange("startDate")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      required: true,
                      error: !!errors.startDate,
                      helperText: errors.startDate,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <DateTimePicker
                  label="End Date"
                  value={formState.endDate}
                  onChange={handleDateChange("endDate")}
                  slotProps={{
                    textField: {
                      fullWidth: true,
                      margin: "normal",
                      required: true,
                      error: !!errors.endDate,
                      helperText: errors.endDate,
                    },
                  }}
                />
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="registrationCode"
                  label="Registration Code"
                  value={formState.registrationCode}
                  onChange={handleChange}
                  margin="normal"
                />
                <Button onClick={() => generateRegistrationCode(8)}>Generate Code</Button>
              </Grid>
              <Grid size={{ xs: 12, sm: 6 }}>
                <TextField
                  fullWidth
                  name="password"
                  label="Course Password"
                  value={formState.password}
                  onChange={handleChange}
                  margin="normal"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <TextField
                  fullWidth
                  required
                  name="maxStudents"
                  label="Max Students"
                  type="number"
                  value={formState.maxStudents}
                  onChange={handleChange}
                  margin="normal"
                  error={!!errors.maxStudents}
                  helperText={errors.maxStudents}
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.openEnroll}
                      onChange={handleSwitchChange}
                      name="openEnroll"
                    />
                  }
                  label="Open Enrollment"
                />
              </Grid>
              <Grid size={{ xs: 6, sm: 3 }}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={formState.visible}
                      onChange={handleSwitchChange}
                      name="visible"
                    />
                  }
                  label="Visible"
                />
              </Grid>
              <Grid size={{ xs: 12 }}>
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<FontAwesomeIcon icon={faCloudArrowUp} />}
                >
                  Upload PDF
                  <VisuallyHiddenInput
                    type="file"
                    onChange={handleFileChange}
                    accept=".pdf"
                  />
                </Button>
                {file && <p>{file.name}</p>}
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          {successMessage ? (
            <Button
              onClick={() => handleCloseLocal({}, "")}
              variant="contained"
              color="primary"
              autoFocus
            >
              Close
            </Button>
          ) : (
            <>
              <Button
                onClick={() => handleCloseLocal({}, "")}
                color="inherit"
                disabled={isLoading}
              >
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                variant="contained"
                color="primary"
                disabled={isLoading}
              >
                {isLoading ? (
                  <CircularProgress size={24} />
                ) : course ? (
                  "Update Course"
                ) : (
                  "Create Course"
                )}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </LocalizationProvider>
  );
};

export default CreateCourseDialog;
